import {
  Box,
  Button,
  Heading,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { createHmacString, detectDevice } from "../../helpers";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Context } from "../../store";
import GoogleWalletButton from "../../components/google-wallet-button";
import AppleWalletButton from "../../components/apple-wallet-button";
import { ACCENT, accentOutline } from "../../accent";
import { parseRegistrationBonus, RegistrationBonus, registrationBonusText } from "../../bonusText";

const BonusCard = ({ bonus }: { bonus: RegistrationBonus }) => {
  const { title, lines } = registrationBonusText(bonus)
  return (
    <Box borderWidth="2px" borderColor={ACCENT} borderRadius="lg" padding={4} textAlign="center">
      <Text fontSize="2xl">🎉</Text>
      <Heading size="md" as="h3" marginBottom={2}>{title}</Heading>
      {lines.map((line) => (
        <Text key={line} color="gray.600">{line}</Text>
      ))}
    </Box>
  )
}

const SetWallet = () => {
  const device = detectDevice()
  const {branch_id, user} = useContext(Context)
  const navigate = useNavigate()
  const toast = useToast()
  const location = useLocation()

  // a restored or copied /set-wallet tab has no customer in memory
  useEffect(() => {
    if (!user) {
      navigate({ pathname: '/', search: location.search }, { replace: true })
    }
  }, [user, navigate, location.search])
  const [loading, setLoading] = useState(false)
  const [passDownloadded, setPassDownloadded] = useState(false)
  const [bonus, setBonus] = useState<RegistrationBonus | null>(null)
  const [googleLink, setGoogleLink] = useState('')

  const showIssueError = () =>
    toast({ title: 'Не удалось выпустить карту, попробуйте ещё раз', status: 'error', duration: 4000 })

  const handleSetWallet = async () => {
    const params = `?branch_id=${branch_id}&user_id=${user?.id}`;
    try {
      setLoading(true)
      const token = createHmacString(new Date().getTime().toString())

      switch (device) {
        case 'ios':
          const responseIOS = await fetch(process.env.REACT_APP_API_URL + `/wallet/pass/ios${params}`, {
            headers: {
              'Content-Type': 'application/vnd.apple.pkpass',
              'Authorization': token,
            },
          })
          if (!responseIOS.ok) {
            showIssueError()
            break
          }
          setBonus(parseRegistrationBonus(responseIOS.headers.get('X-Wallet-Registration-Bonus')))
          const buffer = await responseIOS.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          const file = new Blob([bytes], {
            type: 'application/vnd.apple.pkpass',
          })
          const fileURL = URL.createObjectURL(file)
          const link = document.createElement('a')
          link.href = fileURL
          link.download = fileURL.split('/').pop() || '.pkpass'
          link.click()
          setPassDownloadded(true)
          window.URL.revokeObjectURL(fileURL)
          break;
        case 'android':
        case 'desktop':
          const response = await fetch(process.env.REACT_APP_API_URL + `/wallet/pass/android${params}`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token,
            },
          })
          if (!response.ok) {
            showIssueError()
            break
          }
          const json = await response.json()
          const data = json.data
          if (!data?.link) {
            showIssueError()
            break
          }
          const granted = parseRegistrationBonus(data.registration_bonus)
          if (granted) {
            // the bonus screen would vanish on redirect, so the customer opens Google Wallet from it
            setBonus(granted)
            setGoogleLink(data.link)
            break
          }
          window.location.replace(data.link)
          break;
      }
    } catch (e) {
      showIssueError()
    } finally {
      setLoading(false)
    }
  }

  const walletIcon = (
    <Box stroke={ACCENT} justifyContent="center" display="flex">
      <svg stroke={ACCENT} fill="none" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden="true" height="200px"
           width="200px">
        <path strokeLinecap="round" strokeLinejoin="round"
              d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3"></path>
      </svg>
    </Box>
  )

  if (googleLink && bonus) {
    return (
      <Stack spacing={4} width="100%">
        <BonusCard bonus={bonus} />
        <Text textAlign="center">Осталось сохранить карту — бонусы будут видны на ней</Text>
        <GoogleWalletButton
          onClick={() => window.location.replace(googleLink)}
          textTransform="uppercase"
          colorScheme='teal'
          variant='plain'
        />
      </Stack>
    )
  }

  return (
    <Stack spacing={4}>
      {!passDownloadded ? (
        <>
          {walletIcon}
          <Box>
            <Heading size="md" as="h6" textAlign="center">Для хранения карт необходимо приложение Wallet</Heading>
          </Box>
          {device === 'ios' ? (
            <AppleWalletButton
              onClick={handleSetWallet}
              textTransform="uppercase"
              colorScheme='teal'
              variant='outline'
              isLoading={loading}
            />
            ) : (
            <GoogleWalletButton
              onClick={handleSetWallet}
              textTransform="uppercase"
              colorScheme='teal'
              variant='plain'
              isLoading={loading}
            />
          )}
        </>
      ) : (
        <>
          {bonus ? <BonusCard bonus={bonus} /> : walletIcon}
          <Box>
            <Heading size="md" as="h6" textAlign="center">Wallet добавлен</Heading>
          </Box>
          <Button
            textTransform="uppercase"
            rightIcon={<ArrowForwardIcon/>}
            colorScheme='teal'
            variant='outline'
            sx={accentOutline}
            isLoading={loading}
            onClick={() => {
              window.location.replace('https://sagibonus.com')
            }}
          >
            Разработчик Wallet
          </Button>
        </>
      )}
    </Stack>
  )
}

export default SetWallet
